import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';

import { Col, Container, Row } from 'reactstrap';
import Analytics from '../../../analytics/Analytics';
import FullProduct from '../../../models/product/FullProduct';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import CartStore from '../../../store/CartStore';
import ConfigStore from '../../../store/ConfigStore';
import ProductStore from '../../../store/ProductStore';
import UIStore from '../../../store/UIStore';
import RequestState from '../../../types/RequestState';
import { calculateTotal, roundWithPrecision } from '../../../util/number';
import Icon from '../../common/Icon';
import ProductAddToCart from '../../product/ProductAddToCart';
import { createAnalyticsProducts } from '../../product/utils/matrixSelections';

const NavigationAddToCartRow = ({
  accountStore,
  analytics,
  cartStore,
  configStore,
  productStore,
  uiStore,
  product,
  activeProductId,
}) => {
  const { addToCartRowIsVisible, isDesktop } = uiStore;
  const { mobile: mobileActive, desktop: desktopActive } =
    configStore.topNavigation.addToCartRow;
  const hideAddToCart = accountStore.isViewOnly;
  const addToCartProduct = product.getActualProduct(activeProductId) || product;
  const showMatrix =
    (accountStore.showCollectionProductMatrix ||
      accountStore.showMultiProductMatrix) &&
    (product.isMulti() || product.isCollection);

  const matrixProducts = showMatrix
    ? cartStore.getEffectiveMatrixProducts(product.id)
    : null;

  const quantity = showMatrix
    ? matrixProducts.reduce((sum, p) => sum + p.qty, 0)
    : cartStore.productQuantity;

  const loading = cartStore.state === RequestState.LOADING;

  const disabled = quantity === 0 || loading;

  if (
    !addToCartProduct ||
    !addToCartRowIsVisible ||
    hideAddToCart ||
    (isDesktop && !desktopActive) ||
    (!isDesktop && !mobileActive)
  ) {
    return null;
  }

  const getElementTopPosition = () => {
    if (!uiStore.isDesktop && uiStore.scrollingDown) {
      return 0;
    } else {
      return uiStore.isDesktop &&
        configStore.topNavigation.hideHeaderOnScrollingDown &&
        uiStore.scrollingDown
        ? 0
        : uiStore.headerHeight;
    }
  };

  const pickedCollection =
    productStore.selectedProductColumn && productStore.selectedProductRow
      ? productStore.selectedProductColumn +
        ' ' +
        productStore.selectedProductRow
      : null;

  const handlePopulateCart = () => {
    const products = cartStore.getEffectiveMatrixProducts(product.id);
    if (!products.length) {
      return;
    }

    const analyticsProducts = createAnalyticsProducts(
      products,
      product,
      accountStore.showPricesWithTax
    );

    // Loading + input clear immediately; defer selection wipe (full matrix observer).
    cartStore.setLoading(true);
    cartStore.bumpMatrixInputClearToken(product.id);
    const populatePromise = cartStore.populate(products, showMatrix);
    setTimeout(() => {
      cartStore.clearMatrixSelection(product.id, true);
      cartStore.updateProductQuantity(0);
    }, 0);

    populatePromise.then(() => {
      if (analyticsProducts && analyticsProducts.length > 0) {
        const precision = 4;
        const totalValue = roundWithPrecision(
          calculateTotal(
            analyticsProducts.map((entry) => entry.price * entry.quantity),
            precision
          ),
          precision
        );
        analytics.addToCart(analyticsProducts, totalValue);
        analytics.cartStatus({
          cart: cartStore.cart,
        });
      }
    });
  };

  return (
    <Container
      fluid
      className={classNames(
        'NavigationAddToCartRow',
        'NavigationAddToCartRow__container'
      )}
      style={{ top: getElementTopPosition() }}
    >
      <Row className="NavigationAddToCartRow__row">
        <Col xs={12} className="NavigationAddToCartRow__column">
          <div className="NavigationAddToCartRow__product">
            <span className="NavigationAddToCartRow__product-title">
              {product.name}
            </span>
            {quantity > 0 && (
              <div className="NavigationAddToCartRow__product-quantity">
                {pickedCollection && pickedCollection + ' '}
                {quantity + ' ' + addToCartProduct.stock_unit}
              </div>
            )}
          </div>
          <div className="NavigationAddToCartRow__cart-wrapper">
            {showMatrix ? (
              <Button
                onClick={handlePopulateCart}
                color="primary"
                disabled={disabled}
                className="NavigationAddToCartRow__populate-button"
              >
                {loading ? (
                  <Icon name="circle-o-notch" spin={loading} />
                ) : (
                  <FormattedMessage
                    id="productCollectionMatrix.addSelectedProducts"
                    defaultMessage="Add Selected Products"
                  />
                )}
              </Button>
            ) : (
              <ProductAddToCart
                product={product}
                addToCartProduct={addToCartProduct}
                activeProductId={activeProductId}
                withQuantityPicker={false}
                withServices={false}
                quantity={quantity}
              />
            )}
          </div>
        </Col>
        {!activeProductId && !showMatrix && (
          <Col xs={12} className="NavigationAddToCartRow__not-active">
            <div className="NavigationAddToCartRow__notifier">
              <Icon name="arrow-up" />
              <FormattedMessage
                id="addToCart.selectProductVariant"
                defaultMessage="Please choose the product variant"
              />
            </div>
          </Col>
        )}
      </Row>
    </Container>
  );
};

NavigationAddToCartRow.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  product: modelOf(FullProduct).isRequired,
  activeProductId: PropTypes.string,
};

export default inject(
  'accountStore',
  'analytics',
  'cartStore',
  'configStore',
  'productStore',
  'uiStore'
)(observer(NavigationAddToCartRow));
